/**
 * Reminders - cross-invoice overview (Phase 13 Cluster A)
 *
 * Top-level list of every payment reminder across all invoices. Hooks
 * (`useReminderList`) and card component (`ReminderCard`) are reused from
 * the per-invoice reminder UI; this route is the "global" entry point.
 */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppShell } from '../components/layout/AppShell';
import { EmptyState, LoadingSpinner, Pagination } from '../components/ui';
import { ReminderCard } from '../components/reminders/ReminderCard';
import { useReminderList } from '../hooks/useReminders';

const reminderSearchSchema = z.object({
  page: z.coerce.number().int().positive().optional().default(1),
  level: z.coerce.number().int().min(1).max(3).optional(),
});

type ReminderSearch = z.infer<typeof reminderSearchSchema>;

export const Route = createFileRoute('/reminders')({
  component: RemindersPage,
  validateSearch: reminderSearchSchema,
});

const levelOptions: Array<{ value: ReminderSearch['level'] | undefined; label: string }> = [
  { value: undefined, label: 'Alle Stufen' },
  { value: 1, label: '1. Mahnung' },
  { value: 2, label: '2. Mahnung' },
  { value: 3, label: 'Letzte Mahnung' },
];

function RemindersPage() {
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();

  const { data, isLoading, error } = useReminderList({
    page: search.page,
    limit: 40,
    level: search.level,
  });

  const handlePageChange = (page: number) => {
    navigate({
      search: (prev: ReminderSearch) => ({ ...prev, page }),
      replace: true,
    });
  };

  const handleLevelChange = (level: ReminderSearch['level'] | undefined) => {
    navigate({
      search: (prev: ReminderSearch) => ({ ...prev, level, page: 1 }),
      replace: true,
    });
  };

  return (
    <ProtectedRoute>
      <AppShell>
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-semibold text-white">Mahnungen</h1>
              <p className="text-white/50 mt-1 text-sm">
                {data?.total ?? 0} Mahnung{(data?.total ?? 0) !== 1 ? 'en' : ''}
              </p>
            </div>
          </div>

          {/* Level filter */}
          <div className="flex items-center gap-2 flex-wrap">
            {levelOptions.map((opt) => {
              const active = (search.level ?? undefined) === opt.value;
              return (
                <button
                  key={opt.label}
                  onClick={() => handleLevelChange(opt.value)}
                  aria-pressed={active}
                  className={`px-3 py-1.5 rounded-lg text-sm transition-colors border ${
                    active
                      ? 'bg-white/10 text-white border-white/20'
                      : 'bg-white/3 text-white/60 border-white/10 hover:bg-white/5 hover:text-white/80'
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-400 mb-4">Fehler beim Laden der Mahnungen</p>
          </div>
        ) : (data?.reminders.length ?? 0) === 0 ? (
          <EmptyState
            title={search.level ? 'Keine Treffer' : 'Keine Mahnungen'}
            description={
              search.level
                ? 'Passen Sie den Filter an, um weitere Mahnungen zu sehen.'
                : 'Mahnungen werden aus der Rechnungsansicht erstellt, sobald eine Zahlung überfällig ist.'
            }
          />
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {data?.reminders.map((reminder) => (
                <ReminderCard key={reminder.id} reminder={reminder} showInvoiceInfo />
              ))}
            </div>

            {data && data.totalPages > 1 && (
              <div className="flex justify-center mt-6">
                <Pagination
                  page={search.page ?? 1}
                  totalPages={data.totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}
      </AppShell>
    </ProtectedRoute>
  );
}
